import { useCallback } from "react";

export default function EditActions({
  canResetUserPassword,
  setAdminError,
  texts,
  setPasswordModalUser,
  setUserPasswordForm,
  setUserPasswordNotice,
  canOpenUserEditModal,
  setEditUserModalUser,
  setEditUserForm,
  normalizeUserStatus,
  setEditUserNotice,
  editUserSaving,
  editUserModalUser,
  setEditUserSaving,
  canManageUserAccount,
  canAssignUserPosition,
  accessCapabilitySet,
  adminFetch,
  apiBaseUrl,
  readApiJson,
  fetchAdminData,
  setAdminToast,
  normalizeAdminPermissionMessage,
  editUserForm
}) {
  const openUserPasswordModal = useCallback((userItem) => {
    if (!canResetUserPassword(userItem)) {
      setAdminError(texts.userManagementRestrictedHint || "You can only manage users with a lower role than your own.");
      return;
    }

    setPasswordModalUser({
      id: userItem.id,
      username: String(userItem.username || "").trim(),
      name: String(userItem.name || "").trim()
    });
    setUserPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    setUserPasswordNotice("");
  }, [
    canResetUserPassword,
    setAdminError,
    setPasswordModalUser,
    setUserPasswordForm,
    setUserPasswordNotice,
    texts.userManagementRestrictedHint
  ]);

  const openEditUserModal = useCallback((userItem) => {
    if (!canOpenUserEditModal(userItem)) {
      setAdminError(texts.userManagementRestrictedHint || "You can only manage users with a lower role than your own.");
      return;
    }

    setEditUserModalUser(userItem);
    setEditUserForm({
      name: String(userItem.name || "").trim(),
      email: String(userItem.email || "").trim(),
      phone: String(userItem.phone || "").trim(),
      role: String(userItem.role || "customer").trim() || "customer",
      status: normalizeUserStatus(userItem.status),
      jobTitle: String(userItem.jobTitle || "").trim()
    });
    setEditUserNotice("");
  }, [
    canOpenUserEditModal,
    normalizeUserStatus,
    setAdminError,
    setEditUserForm,
    setEditUserModalUser,
    setEditUserNotice,
    texts.userManagementRestrictedHint
  ]);

  const closeEditUserModal = useCallback(() => {
    if (editUserSaving) {
      return;
    }
    setEditUserModalUser(null);
    setEditUserNotice("");
  }, [editUserSaving, setEditUserModalUser, setEditUserNotice]);

  const submitEditUser = useCallback(async (event) => {
    event.preventDefault();

    if (!editUserModalUser?.id) {
      setEditUserNotice("User context is missing.");
      return;
    }

    setEditUserSaving(true);
    setEditUserNotice("");

    try {
      const canEditAccount = canManageUserAccount(editUserModalUser);
      const canAssignPosition = canAssignUserPosition(editUserModalUser);
      if (!canEditAccount && !canAssignPosition) {
        setEditUserNotice(texts.userManagementRestrictedHint || "You can only manage users with a lower role than your own.");
        setEditUserSaving(false);
        return;
      }

      const originalStatus = normalizeUserStatus(editUserModalUser.status);
      const requestPayload = {};

      if (canEditAccount && String(editUserForm.name || "").trim() !== String(editUserModalUser.name || "").trim()) {
        requestPayload.name = editUserForm.name;
      }
      if (canEditAccount && String(editUserForm.email || "").trim() !== String(editUserModalUser.email || "").trim()) {
        requestPayload.email = editUserForm.email;
      }
      if (canEditAccount && String(editUserForm.phone || "").trim() !== String(editUserModalUser.phone || "").trim()) {
        requestPayload.phone = editUserForm.phone;
      }
      if (
        canEditAccount &&
        accessCapabilitySet.has("admin:users:role:write") &&
        (String(editUserForm.role || "customer").trim() || "customer") !== (String(editUserModalUser.role || "customer").trim() || "customer")
      ) {
        requestPayload.role = editUserForm.role;
      }
      if (canEditAccount && normalizeUserStatus(editUserForm.status) !== originalStatus) {
        requestPayload.status = editUserForm.status;
      }
      if (canAssignPosition && String(editUserForm.jobTitle || "").trim() !== String(editUserModalUser.jobTitle || "").trim()) {
        requestPayload.jobTitle = editUserForm.jobTitle;
      }

      if (!Object.keys(requestPayload).length) {
        setEditUserNotice(texts.none);
        setEditUserSaving(false);
        return;
      }

      const response = await adminFetch(`${apiBaseUrl}/admin/users/${editUserModalUser.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestPayload)
      });

      const data = await readApiJson(response);
      if (!response.ok) {
        throw new Error(data.message || "User update failed");
      }

      await fetchAdminData();
      setAdminToast(texts.userUpdated || texts.save);
      closeEditUserModal();
    } catch (err) {
      if (err.message !== "Session expired") {
        setEditUserNotice(normalizeAdminPermissionMessage(err.message, "User update failed"));
      }
    } finally {
      setEditUserSaving(false);
    }
  }, [
    accessCapabilitySet,
    adminFetch,
    apiBaseUrl,
    canAssignUserPosition,
    canManageUserAccount,
    closeEditUserModal,
    editUserForm,
    editUserModalUser,
    fetchAdminData,
    normalizeAdminPermissionMessage,
    normalizeUserStatus,
    readApiJson,
    setAdminToast,
    setEditUserNotice,
    setEditUserSaving,
    texts.none,
    texts.save,
    texts.userManagementRestrictedHint,
    texts.userUpdated
  ]);

  return {
    openUserPasswordModal,
    openEditUserModal,
    closeEditUserModal,
    submitEditUser
  };
}
